import { Navigate } from 'react-router';

// common
import ParentBlockedRoute from 'common/components/ParentBlockedRoute';
import { IRouteObject } from 'common/types';
import { ROUTES_PATH } from 'layout/constants';
import DashboardPage from 'modules/dashboard/DashboardPage';

// Views
// Not found
import NotFoundPage from 'modules/NotFoundPage';

// Map
import NewMap from 'modules/map/NewMap';

// Đơn vị
import DepartmentDetailPage from 'modules/department/detail';
import DepartmentPage from 'modules/department/list';

// Tài khoản
import AccountPage from 'modules/account/list';
import UserInfoDetailPage from 'modules/user-info/detail';
import AccountCreatePage from 'modules/account/create';
import AccountDetailPage from 'modules/account/detail';

// QR NCC TPAT
import SupplierListPage from 'modules/suppliers/list/SupplierListPage';
import SupplierDetailPage from 'modules/suppliers/detail/SupplierDetailPage';

// Tìm kiếm nhanh (Quick Search)
import QuickSearchPage from 'modules/quick-search/QuickSearchPage';

// Mã xác nhận (phụ huynh)
import ConfirmationCodeListPage from 'modules/confirmation-code/list';
import ConfirmationCodeCreatePage from 'modules/confirmation-code/create';
import ConfirmationCodeDetailPage from 'modules/confirmation-code/detail';

// Bộ icon điều hướng: nét mảnh, màu theo trạng thái ở DefaultAsideItems.
import {
  Building2,
  KeyRound,
  LayoutDashboard,
  Map as MapIcon,
  QrCode,
  ScanSearch,
  UserCog,
} from 'lucide';
import Icon from 'common/components/Icon';

export const PROJECT_LIST_ROUTES: IRouteObject[] = [
  {
    path: '',
    element: <Navigate to={`${ROUTES_PATH.portal.index}/${ROUTES_PATH.dashboard.index}`} replace />,
    hidden: true,
  },
  {
    title: 'user.title',
    path: ROUTES_PATH.profile,
    element: <UserInfoDetailPage />,
    hidden: true,
  },
  {
    path: ROUTES_PATH.portal.index,
    title: 'portal.title',
    children: [
      {
        title: 'dashboard.title',
        path: ROUTES_PATH.dashboard.index,
        icon: <Icon icon={LayoutDashboard} size={20} />,
        element: <DashboardPage />,
      },
      {
        title: 'quickSearch.title',
        path: ROUTES_PATH.quickSearch.index,
        icon: <Icon icon={ScanSearch} size={20} />,
        children: [
          {
            index: true,
            element: <QuickSearchPage />,
          },
          {
            // Chi tiết rút gọn (UC3) — toàn hệ thống theo id NCC, không cần khách hàng.
            label: 'quickSearch.detail',
            path: ':id/view-only',
            // Phụ huynh: chỉ xem danh sách, chặn vào chi tiết (kể cả gõ URL thẳng).
            element: (
              <ParentBlockedRoute>
                <SupplierDetailPage />
              </ParentBlockedRoute>
            ),
          },
        ],
      },
      {
        title: 'map.title',
        path: ROUTES_PATH.menuMap.index,
        icon: <Icon icon={MapIcon} size={20} />,
        element: <NewMap />,
      },
      {
        title: 'supplier.title',
        path: ROUTES_PATH.supplier.index,
        label: 'supplier.label',
        icon: <Icon icon={QrCode} size={20} />,
        children: [
          {
            index: true,
            // Phụ huynh không có QR NCC TPAT (FSOL) — chặn cả list lẫn chi tiết.
            element: (
              <ParentBlockedRoute>
                <SupplierListPage />
              </ParentBlockedRoute>
            ),
          },
          {
            label: 'supplier.detail',
            path: 'tab/:selectedTab/:customerOperationCode/dang-cung-cap/:supplierOperationCode/view-only',
            element: (
              <ParentBlockedRoute>
                <SupplierDetailPage />
              </ParentBlockedRoute>
            ),
          },
        ],
      },
    ],
  },
  {
    path: ROUTES_PATH.ad.index,
    title: 'ad.title',
    children: [
      {
        path: ROUTES_PATH.department.index,
        title: 'department.title',
        label: 'department.label',
        icon: <Icon icon={Building2} size={20} />,
        children: [
          {
            index: true,
            element: <DepartmentPage />,
          },
          {
            label: 'department.create',
            path: ROUTES_PATH.department.create,
            element: <DepartmentDetailPage />,
          },
          {
            label: 'department.detail',
            path: ROUTES_PATH.department.detail,
            element: <DepartmentDetailPage />,
          },
        ],
      },
      {
        path: ROUTES_PATH.account.index,
        title: 'account.title',
        label: 'account.label',
        icon: <Icon icon={UserCog} size={20} />,
        children: [
          {
            index: true,
            element: <AccountPage />,
          },
          {
            label: 'account.create',
            path: ROUTES_PATH.account.create,
            element: <AccountCreatePage />,
          },
          {
            label: 'account.page.edit',
            path: ROUTES_PATH.account.detail,
            element: <AccountDetailPage />,
          },
          {
            label: 'account.detail',
            path: ROUTES_PATH.account.viewOnly,
            element: <AccountDetailPage viewOnly={true} />,
          },
        ],
      },
      {
        path: ROUTES_PATH.confirmationCode.index,
        title: 'confirmationCode.title',
        label: 'confirmationCode.label',
        icon: <Icon icon={KeyRound} size={20} />,
        children: [
          {
            index: true,
            element: <ConfirmationCodeListPage />,
          },
          {
            label: 'confirmationCode.create',
            path: ROUTES_PATH.confirmationCode.create,
            element: <ConfirmationCodeCreatePage />,
          },
          {
            label: 'confirmationCode.detail',
            path: ROUTES_PATH.confirmationCode.detail,
            element: <ConfirmationCodeDetailPage />,
          },
        ],
      },
    ],
  },
  { path: ROUTES_PATH[404], element: <NotFoundPage />, hidden: true },
  {
    // HIỆN trang 404 tại chỗ, KHÔNG `Navigate` sang /404: cây route được lọc theo
    // `auth.userRoles` (routesByUserRoles trong DefaultLayout), nên khi vào thẳng một URL quản trị
    // lúc roles chưa về (máy mới / vừa xoá localStorage nên redux-persist không có gì để phục
    // hồi), nhánh 'ad' tạm thời chưa tồn tại và request rơi vào đây. Nếu ghi lại URL thành /404
    // thì roles về sau đó cũng vô nghĩa — người dùng kẹt ở 404 vĩnh viễn. Giữ nguyên URL thì cây
    // route dựng lại là trang đúng tự hiện ra.
    path: '*',
    element: <NotFoundPage />,
    hidden: true,
  },
];
